import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import { message, Spin } from 'antd'
import { HiOutlineEye, HiOutlineEyeSlash, HiMiniXCircle, HiCheckCircle } from 'react-icons/hi2'
import ReCAPTCHA from 'react-google-recaptcha'

import './style.scss'
import uris from '../../../config/uris'
import { encrypt_forge } from '../../../helpers/encrypt/forge-encrypt'
import { encryptData } from '../../../helpers/encrypt/crypto-encrypt'
import { SignUpAction, SignUpReset } from '../../../store/slices/auth/signupSlice'

const refLinks = {
    termsOfUse: 'https://www.esgsure.com/wp-content/uploads/2024/09/privicy-policy.pdf',
    privacyPolicy: 'https://www.esgsure.com/wp-content/uploads/2024/09/terms-of-use.pdf',
}

const SignUp = () => {
    const [isChecked, setIsChecked] = useState(false)
    const [isCaptchaChecked, setIsCaptchaChecked] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const [isPasswordFieldTouched, setIsPasswordFieldTouched] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [isConfirmPasswordTouched, setIsConfirmPasswordTouched] = useState(true)
    const [captchaError, setCaptchaError] = useState(' ')
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        orgName: '',
        password: '',
        confirmPassword: '',
    })
    const [additionalformDatas, setAdditionalformDatas] = useState({
        isTrial: true,
        country: null,
        pack: null,
        billingPeriod: null,
    })

    const [formDataErrors, setformDataErrors] = useState({
        firstName: '',
        lastName: '',
        email: '',
        orgName: '',
        password: '',
        confirmPassword: '',
        agreeTerms: '',
        passwordConditions: [false, false, false, false, false, false],
        isPasswordValid: false,
        isConfirmPasswordMatched: null,
    })

    const errorMessages = {
        firstName: '*Enter Firstname',
        lastName: '*Enter Lastname',
        email: '*Enter a valid email',
        password: [
            'Password should contain atleast 8-25 characters',
            'Password should contain atleast one uppercase letter[A-Z]',
            'Password should contain atleast one lowercase letter[a-z]',
            'Password should contain atleast one number[0-9]',
            'Password should contain atleast one special Character[! @ # $ %]',
            'Password should not contain any other characters',
        ],
        confirmPassword: !isConfirmPasswordTouched ? '*Passwords does not match' : '*Enter a valid password',
    }

    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { search: stripeValue } = useLocation()

    const { data: signUpData, success: signUpSuccess, error: signUpError, isLoading: signUpLoading } = useSelector((state) => state.signUp)
    const isDevPlatform = process.env.REACT_APP_ENVIRONMENT === 'dev' || process.env.REACT_APP_ENVIRONMENT === 'local';

    const handleFormValue = (input, value) => {
        setFormData((prevFormData) => ({
            ...prevFormData,
            [input]: value,
        }))

        if (input === 'password') {
            setIsPasswordFieldTouched(value.length > 0)

            const conditions = [value.length >= 8 && value.length <= 25, /[A-Z]/.test(value), /[a-z]/.test(value), /\d/.test(value), /[!@#$%]/.test(value), /^[A-Za-z0-9!@#$%]+$/.test(value)]
            setformDataErrors((prevValues) => ({
                ...prevValues,
                passwordConditions: conditions,
                isPasswordValid: conditions.every(Boolean),
            }))

            if (formData.confirmPassword) {
                const isConfirmPasswordMatched = value === formData.confirmPassword
                setIsConfirmPasswordTouched(true)
                setformDataErrors((prevValues) => ({
                    ...prevValues,
                    confirmPassword: isConfirmPasswordMatched ? 'Password matched' : 'Passwords do not match',
                    isConfirmPasswordMatched,
                }))
            }
        } else if (input === 'confirmPassword') {
            setIsConfirmPasswordTouched(true)
            const isConfirmPasswordMatched = value === formData.password
            setformDataErrors((prevValues) => ({
                ...prevValues,
                confirmPassword: value ? (isConfirmPasswordMatched ? 'Password matched' : 'Passwords do not match') : 'Password field cannot be empty',
                isConfirmPasswordMatched,
            }))
        }
    }

    const handleTermsAndCondition = () => {
        setIsChecked(!isChecked)
    }

    const handleVisiblePassword = (value) => {
        if (value === 'password') {
            setShowPassword(!showPassword)
        } else {
            setShowConfirmPassword(!showConfirmPassword)
        }
    }

    const handleLoginNavigate = () => {
        navigate(uris?.login)
    }

    const onCaptchaChange = () => {
        setIsCaptchaChecked(true)
    }

    const validateEmail = (emailmsg) => {
        const regex = /\S+@\S+\.\S+/
        return regex.test(emailmsg)
    }

    const validateForm = () => {
        let isFormValid = true
        const errors = { ...formDataErrors }

        if (!isChecked) {
            errors.agreeTerms = 'required'
            isFormValid = false
        } else {
            errors.agreeTerms = ''
        }

        for (const key in formData) {
            const value = formData[key]

            if (key === 'password') {
                if (!value) {
                    errors[key] = '*Enter a valid password'
                    isFormValid = false
                } else if (value.length < 8) {
                    errors[key] = '*Password must contain at least 8 characters'
                    isFormValid = false
                } else {
                    errors[key] = ''
                }
            } else if (key === 'email' && !validateEmail(value)) {
                errors[key] = errorMessages[key]
                isFormValid = false
            } else if (!value && key !== 'confirmPassword' && key !== 'orgName') {
                errors[key] = errorMessages[key]
                isFormValid = false
            } else if (key === 'confirmPassword') {
                if (!value) {
                    errors[key] = errorMessages[key]
                    errors.isConfirmPasswordMatched = null
                    setIsConfirmPasswordTouched(true)
                    isFormValid = false
                } else {
                    errors[key] = formDataErrors.confirmPassword || ''
                }
            } else {
                errors[key] = ''
            }
        }
        if (!formDataErrors?.isPasswordValid || !formDataErrors?.isConfirmPasswordMatched) {
            isFormValid = false
        }

        setformDataErrors(errors)
        return isFormValid
    }

    const handleRegister = () => {
        if (validateForm() && validateEmail(formData.email) && isConfirmPasswordTouched && isChecked && isCaptchaChecked) {
            let encrypted_user = encrypt_forge(formData)
            let encrypted_plan = encrypt_forge(additionalformDatas)

            const payload = {
                login: { userDetails: encrypted_user, planDetails: encrypted_plan },
            }
            dispatch(SignUpReset())
            dispatch(SignUpAction(payload))
        } else {
            !isCaptchaChecked ? setCaptchaError('*Please verify that you are not a robot.') : setCaptchaError(' ')
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleRegister()
        }
    }

    useEffect(() => {
        if (signUpSuccess && signUpData) {
            if (signUpData?.url) {
                let stripeCheckoutUrl = encryptData(signUpData?.url)
                sessionStorage.stripeCheckoutUrl = stripeCheckoutUrl
            }
            message.success(signUpData?.message)
            navigate(uris?.login)
            dispatch(SignUpReset())
        }
    }, [signUpSuccess, signUpData])

    useEffect(() => {
        if (signUpError) {
            message.error(signUpError?.message)
            dispatch(SignUpReset())
        }
    }, [signUpError])

    useEffect(() => {
        if (stripeValue) {
            const params = new URLSearchParams(stripeValue)
            const packValue = params.get('pack')
            const billingPeriodValue = params.get('billingPeriod')

            let stripeValue = encryptData({ pack: packValue, billingPeriod: billingPeriodValue })
            sessionStorage.stripeValue = stripeValue

            setAdditionalformDatas((prevDatas) => ({
                ...prevDatas,
                isTrial: false,
                pack: packValue,
                billingPeriod: billingPeriodValue,
            }))
        }
    }, [stripeValue])

    useEffect(() => {
        if (isDevPlatform) {
            setIsCaptchaChecked(true);
        }
    }, [isDevPlatform])

    return (
        <div className="signup-page">
            <div className="signup-form-container">
                <div className="header-section">
                    <div className="form-header">Register</div>
                </div>

                <div className="form-contents">
                    <div className="input-wrapper">
                        <div className="firstname-section">
                            <div className="label">
                                First Name <span>*</span>
                            </div>
                            <div className="input-area">
                                <div className="overlap-group">
                                    <input
                                        className="form-input"
                                        name="firstname"
                                        type="text"
                                        placeholder="Enter your FirstName"
                                        value={formData.firstName}
                                        onChange={(e) => handleFormValue('firstName', e?.target?.value)}
                                        onKeyDown={(e) => handleKeyDown(e)}
                                    />
                                </div>
                            </div>
                            <div className="error-message">{formDataErrors.firstName}</div>
                        </div>
                        <div className="lastname-section">
                            <div className="label">
                                Last Name <span>*</span>
                            </div>
                            <div className="input-area">
                                <div className="overlap-group">
                                    <input
                                        className="form-input"
                                        name="lastname"
                                        type="text"
                                        placeholder="Enter your LastName"
                                        value={formData.lastName}
                                        onChange={(e) => handleFormValue('lastName', e?.target?.value)}
                                        onKeyDown={(e) => handleKeyDown(e)}
                                    />
                                </div>
                            </div>
                            <div className="error-message">{formDataErrors.lastName}</div>
                        </div>
                    </div>
                    <div className="input-wrapper">
                        <div className="email-section">
                            <div className="label">
                                Email <span>*</span>
                            </div>
                            <div className="input-area">
                                <div className="overlap-group">
                                    <input
                                        className="form-input"
                                        name="email"
                                        type="text"
                                        placeholder="Enter your Email"
                                        value={formData.email}
                                        onChange={(e) => handleFormValue('email', e?.target?.value)}
                                        onKeyDown={(e) => handleKeyDown(e)}
                                    />
                                </div>
                            </div>
                            <div className="error-message">{formDataErrors.email}</div>
                        </div>
                        <div className="org-name-section">
                            <div className="label">
                                Organization Name <span></span>
                            </div>
                            <div className="input-area">
                                <div className="overlap-group">
                                    <input
                                        className="form-input"
                                        name="orgName"
                                        type="text"
                                        placeholder="Enter your Organization Name"
                                        value={formData.orgName}
                                        onChange={(e) => handleFormValue('orgName', e?.target?.value)}
                                        onKeyDown={(e) => handleKeyDown(e)}
                                    />
                                </div>
                            </div>
                            <div className="error-message">{formDataErrors.orgName}</div>
                        </div>
                    </div>
                    <div className="input-wrapper">
                        <div className="password-section">
                            <div className="label">
                                Password <span>*</span>
                            </div>
                            <div className="input-area">
                                <div className="overlap-group">
                                    <input
                                        className="form-input"
                                        name="password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="Enter your Password"
                                        value={formData.password}
                                        onChange={(e) => {
                                            if (!isPasswordFieldTouched) setIsPasswordFieldTouched(true)
                                            handleFormValue('password', e?.target?.value)
                                        }}
                                        onKeyDown={(e) => handleKeyDown(e)}
                                    />
                                    <div className="password-hide-icon" onClick={() => handleVisiblePassword('password')}>
                                        {showPassword ? <HiOutlineEyeSlash /> : <HiOutlineEye />}
                                    </div>
                                </div>
                            </div>
                            {!isPasswordFieldTouched && <div className="error-message">{formDataErrors.password}</div>}
                            {isPasswordFieldTouched && (
                                <div className="initial-error-message">
                                    {formDataErrors?.passwordConditions?.every(Boolean) ? (
                                        <div className='error-item success-icon'><HiCheckCircle /> Password Criteria Met</div>
                                    ) : (
                                    errorMessages.password.map((error, index) => (
                                        <div key={index} className={`error-item ${formDataErrors.passwordConditions[index] ? 'success-icon' : 'error-icon'}`}>
                                            {formDataErrors.passwordConditions[index] ? (
                                                <span>
                                                    {' '}
                                                    <HiCheckCircle /> {error}{' '}
                                                </span>
                                            ) : (
                                                <span>
                                                    {' '}
                                                    <HiMiniXCircle /> {error}{' '}
                                                </span>
                                            )}
                                        </div>
                                    )))}
                                </div>
                            )}
                        </div>
                        <div className="confirm-password-section">
                            <div className="label">
                                Confirm Password <span>*</span>
                            </div>
                            <div className="input-area">
                                <div className={`overlap-group ${!isConfirmPasswordTouched && 'password-mismatch'}`}>
                                    <input
                                        className="form-input"
                                        name="confirmPassword"
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        placeholder="Enter your Confirm Password"
                                        value={formData.confirmPassword}
                                        onChange={(e) => handleFormValue('confirmPassword', e?.target?.value)}
                                        onKeyDown={(e) => handleKeyDown(e)}
                                    />
                                    <div className="password-hide-icon" onClick={() => handleVisiblePassword('confirmPassword')}>
                                        {showConfirmPassword ? <HiOutlineEyeSlash /> : <HiOutlineEye />}
                                    </div>
                                </div>
                            </div>
                            <div className="confirm-password-error-section">
                                {formDataErrors.isConfirmPasswordMatched !== null ? (
                                    <>
                                        <div className={`confirm-password-message ${!formDataErrors.isConfirmPasswordMatched ? 'error-icon' : 'success-icon'}`}>
                                            {!formDataErrors.isConfirmPasswordMatched ? <HiMiniXCircle /> : <HiCheckCircle />}
                                        </div>
                                        <div className={`confirm-password-message ${!formDataErrors.isConfirmPasswordMatched ? 'error-icon' : 'success-icon'}`}>{formDataErrors.confirmPassword}</div>
                                    </>
                                ) : (
                                    <div className={`error-message`}>{formDataErrors.confirmPassword}</div>
                                )}
                            </div>
                        </div>
                    </div>
                    {process.env.REACT_APP_ENVIRONMENT === "production" && (
                        <div className="input-wrapper">
                        <div className="recaptcha-section">
                            <ReCAPTCHA
                                name="verified"
                                className={`recaptcha ${isCaptchaChecked ? 'red-class' : ''}`}
                                sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA}
                                onChange={(e) => onCaptchaChange(e)}
                                size="normal"
                                theme="light"
                                onExpired={() => setIsCaptchaChecked(false)}
                                onError={() => setIsCaptchaChecked(false)}
                            />
                            {captchaError && <div className="error-message">{captchaError}</div>}
                        </div>
                    </div>)}
                    <div className="input-wrapper">
                        <div className="agree-section">
                            <div className="input-checkbox">
                                <span tabIndex={0} onClick={() => handleTermsAndCondition()} className={`custom-checkbox ${isChecked ? 'checked' : formDataErrors.agreeTerms ? 'error' : ''}`}></span>
                            </div>
                            <div className="agree-section-text">
                                I have read and agree to the{' '}
                                <a href={refLinks?.termsOfUse} target="_blank" className="terms-link">
                                    Terms of Use
                                </a>{' '}
                                and the{' '}
                                <a href={refLinks?.privacyPolicy} target="_blank" className="terms-link">
                                    Privacy Policy
                                </a>
                                <span className="required-icon">*</span>
                            </div>
                        </div>
                    </div>
                    <div className="form-button-wrapper">
                        <div className={`form-button ${signUpLoading && 'button-disable'}`} onClick={() => handleRegister()}>
                            <div className="text-wrapper">Start Trial {stripeValue && ' & Subscribe'}</div>
                        </div>
                        <div className="exists-account-section">
                            <div className="">Already have an account ?</div>
                            <div className="login-text" onClick={() => handleLoginNavigate()}>
                                Login
                            </div>
                        </div>
                    </div>
                </div>
                {signUpLoading && (
                    <div className="register-loader-section">
                        <div className="spin">
                            <Spin />
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default SignUp
